import { useGenerateImage } from '@/components/Analysis/modules/thumbnails';
import { CircularProgress, Typography } from '@mui/material';
import { ImageFallback, ImageThumbnailImgLoader } from './styles';
import { useEffect, useMemo, useState } from 'react';
import {
  GenerateCropFromRectilinearInput,
  GenerateImageInput,
  Pose,
} from '@/__generated__/graphql';
import { GetStructureLocationsForInspectionDataLoaderQuery } from '@/__generated__/graphql';
import { Number3 } from '@abyss/3d-viewer';
import { defaultThumbnailConfig } from '@/constants';
import * as THREE from 'three';
import { Euler, Quaternion, Spherical, Vector3 } from 'three';

export type ThumbnailConfig = {
  viewHeightMeters: number;
  width: number;
  height: number;
};

const getOutputVerticalFov = (pose: Pose, lookAt: Number3) => {
  const frustumHeight = 0.7; // Could be editable by slider in the future
  const pointVector = new THREE.Vector3(lookAt[0], lookAt[1], lookAt[2]);
  const cameraPosition = new THREE.Vector3(pose.x, pose.y, pose.z);
  const distance = cameraPosition.distanceTo(pointVector);
  const fovRadians = 2 * Math.atan(frustumHeight / (2 * distance));
  return fovRadians;
};
type Location = GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0];
const locationPolarFromCartesian = (location: Location, lookAt: Number3) => {
  if (!location.pose) return undefined;

  const { x, y, z, roll, pitch, yaw, yawOffset } = location.pose;
  const euler = new Euler(roll, pitch, yaw, 'ZYX');
  const eulerUpYtoUpZ = new Euler(-Math.PI / 2, 0, 0, 'XYZ');
  const imageEuler = new Euler(0, 0, -(yawOffset ?? 0), 'ZYX');
  const quat = new Quaternion().setFromEuler(euler);
  const quat2 = new Quaternion().setFromEuler(eulerUpYtoUpZ);
  const imageQuat = new Quaternion().setFromEuler(imageEuler);
  quat.multiply(imageQuat).invert();
  const sphericalPoint = new Spherical();
  const point = new Vector3(lookAt[0] - x, lookAt[1] - y, lookAt[2] - z);
  point.applyQuaternion(quat);
  point.applyQuaternion(quat2);
  sphericalPoint.setFromVector3(point);

  // const u = -sphericalPoint.theta / (Math.PI * 2);
  // const v = sphericalPoint.phi / Math.PI;
  // const data = convertImagePointToPolar(u, v);

  const bearing = (Math.PI - sphericalPoint.theta) % (2 * Math.PI);
  const elevation = Math.PI / 2 - sphericalPoint.phi;
  return {
    bearing,
    elevation,
    radius: sphericalPoint.radius,
  };
};

const mapRectilinear = (
  location: Location,
  lookAt: Number3,
  outputWidth: number,
  outputHeight: number
): GenerateCropFromRectilinearInput | undefined => {
  const { pose, imageSensor } = location;
  if (!pose || !imageSensor) return;
  return {
    centerPoint: { x: lookAt[0], y: lookAt[1], z: lookAt[2] },
    image: {
      pose: {
        pitch: pose.pitch,
        roll: pose.roll,
        x: pose.x,
        y: pose.y,
        yaw: pose.yaw,
        z: pose.z,
      },
      url: location.resourcePath ?? '',
      imageSensor: {
        cx: imageSensor.cx,
        cy: imageSensor.cy,
        focalLength: imageSensor.focalLength,
        height: imageSensor.height,
        width: imageSensor.width,
      },
    },
    outputVerticalFov: pose ? getOutputVerticalFov(pose, lookAt) : 0,
    outputHeight,
    outputWidth,
  };
};

const mapSpherical = (
  location: Location,
  lookAt: Number3,
  outputWidth: number,
  outputHeight: number
): GenerateImageInput | undefined => {
  const polar = locationPolarFromCartesian(location, lookAt);
  if (!polar) return;

  const distance = polar.radius;
  const verticalFovDegrees =
    (2 * Math.atan(defaultThumbnailConfig.viewHeightMeters / (2 * distance)) * 180) / Math.PI;

  return {
    imageName: location.name,
    imageUrl: location.resourcePath ?? '',
    locationId: location.id,
    bearing: polar.bearing,
    elevation: polar.elevation,
    verticalFovDegrees,
    outputHeight,
    outputWidth,
  };
};

export const thumbnailInputFromLookAt = (
  viewpoint: GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0] | undefined,
  lookAt: Number3 | undefined
): GenerateImageInput | GenerateCropFromRectilinearInput | undefined => {
  if (!viewpoint) return undefined;
  if (!viewpoint.resourcePath) return undefined;
  if (!lookAt) return undefined;
  const outputWidth = defaultThumbnailConfig.width;
  const outputHeight = defaultThumbnailConfig.height;

  // rectilinear Image Mapping
  if (viewpoint.imageSensor && viewpoint.pose) {
    return mapRectilinear(viewpoint, lookAt, outputWidth, outputHeight);
  }

  return mapSpherical(viewpoint, lookAt, outputWidth, outputHeight);
};

type Props = {
  location: GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0] | undefined;
  lookAt: Number3 | undefined;
  thumbnailHeight?: number;
  handleLoading?: (loading: boolean) => void;
  sx?: React.CSSProperties;
};

export const ViewpointThumbnail = ({
  location,
  lookAt,
  thumbnailHeight,
  handleLoading,
  sx = {},
}: Props) => {
  const [imageData, setImageData] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    handleLoading?.(loading);
  }, [handleLoading, loading]);

  const { fetchCropFromRectilinear, fetchCropFromSpherical } = useGenerateImage();

  const input = useMemo(() => {
    return thumbnailInputFromLookAt(location, lookAt);
  }, [location, lookAt]);

  useEffect(() => {
    const isRectilinear = input && 'image' in input;
    if (input && isRectilinear) {
      fetchCropFromRectilinear(input).then((data) => {
        setImageData(data);
        setLoading(false);
      });
    } else if (input && !isRectilinear) {
      fetchCropFromSpherical(input).then((data) => {
        setImageData(data);
        setLoading(false);
      });
    }
  }, [fetchCropFromRectilinear, fetchCropFromSpherical, input]);

  return (
    <>
      {loading && (
        <ImageThumbnailImgLoader
          sx={{ ...(thumbnailHeight ? { minHeight: thumbnailHeight } : {}) }}
        >
          <CircularProgress size="5rem" />
        </ImageThumbnailImgLoader>
      )}
      {!loading && imageData && (
        <img
          style={{ borderRadius: 5, border: '2px solid #1A1B1C', ...sx }}
          src={`data:image/jpeg;base64,${imageData}`}
          width="100%"
          height="100%"
        />
      )}

      {!loading && !imageData && (
        <ImageFallback>
          <Typography variant="h6">No Image Available</Typography>
        </ImageFallback>
      )}
    </>
  );
};
